import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { AuthService } from './auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { InactivityWarningModalComponent } from '../../../shared/components/inactivity-warning/inactivity-warning-modal.component';

@Injectable({ providedIn: 'root' })
export class AutoLogoutService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private zone = inject(NgZone);
  private modalService = inject(NgbModal);

  private userActivityEvents = [
    'mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'
  ];
  private inactivityTimeout = 15 * 60 * 1000; 
  private warningTimeout = 3 * 60 * 1000; 
  private activitySub?: Subscription;
  private inactivityTimerSub?: Subscription;
  private warningTimerSub?: Subscription;
  private modalRef: any;
  private visibilityChangeSub?: Subscription;
  private hiddenTimestamp: number | null = null;
  private backgroundTimeout = 15 * 60 * 1000;

  startMonitoring() {
    this.zone.runOutsideAngular(() => {
      const activity$ = merge(
        ...this.userActivityEvents.map(event => fromEvent(window, event))
      );
      this.activitySub = activity$.subscribe(() => this.resetInactivityTimer());
      this.visibilityChangeSub = fromEvent(document, 'visibilitychange').subscribe(() => this.handleVisibilityChange());
    });
    this.resetInactivityTimer();
  }

  private resetInactivityTimer() {
    if (this.inactivityTimerSub) this.inactivityTimerSub.unsubscribe();
    if (this.warningTimerSub) this.warningTimerSub.unsubscribe();
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    this.inactivityTimerSub = timer(this.inactivityTimeout - this.warningTimeout).subscribe(() => this.showWarningModal());
  }

  private showWarningModal() {
    this.zone.run(() => {
      this.modalRef = this.modalService.open(InactivityWarningModalComponent, {
        backdrop: 'static',
        keyboard: false,
        centered: true
      });
      this.modalRef.componentInstance.minutes = Math.round(this.warningTimeout / 60000) || 1;
      this.modalRef.result.then(
        (result: string) => {
          if (result === 'cancel') {
            this.resetInactivityTimer();
          }
        },
        () => {}
      );
      this.warningTimerSub = timer(this.warningTimeout).subscribe(() => this.logout());
    });
  }

  private logout() {
    this.stopMonitoring();
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    this.authService.logout();
    this.router.navigate(['/authentication/login'], { queryParams: { disconnected: 'inactivity' } });
  }

  private handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.hiddenTimestamp = Date.now();
    } else if (document.visibilityState === 'visible' && this.hiddenTimestamp) {
      const hiddenDuration = Date.now() - this.hiddenTimestamp;
      if (hiddenDuration > this.backgroundTimeout) {
        this.logout();
      }
      this.hiddenTimestamp = null;
    }
  }

  stopMonitoring() {
    this.activitySub?.unsubscribe();
    this.inactivityTimerSub?.unsubscribe();
    this.warningTimerSub?.unsubscribe();
    this.visibilityChangeSub?.unsubscribe();
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
  }
}
